/* PLAY NATION EXCLUSIVES | V6.6 Kinetic Boutique / Moments-Inspired Collector View
   Shopify product data stays in Liquid. Isolated to homepage showcase.
   Native JS/CSS only; no Three.js, WebGL, CDN or checkout modifications. */
(() => {
  'use strict';
  const mounted = new WeakSet();
  const TAU = Math.PI * 2;
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const now = () => performance.now();
  const isShopifyEditor = () => window.Shopify?.designMode === true;

  function boot(scope = document) {
    scope.querySelectorAll('[data-pne-boutique]').forEach(init);
    if (scope.matches?.('[data-pne-boutique]')) init(scope);
  }
  function init(root) {
    if (mounted.has(root)) return;
    const frame = root.querySelector('[data-pne-boutique-frame]');
    const field = root.querySelector('[data-pne-boutique-field]');
    const tiles = [...root.querySelectorAll('[data-pne-boutique-tile]')];
    if (!frame || !field || !tiles.length) return;
    mounted.add(root);
    field.classList.add('is-3d');

    const focus = root.querySelector('[data-pne-boutique-focus]');
    const modalImage = root.querySelector('[data-pne-boutique-focus-image]');
    const modalTitle = root.querySelector('[data-pne-boutique-focus-title]');
    const modalStatus = root.querySelector('[data-pne-boutique-focus-status]');
    const modalPrice = root.querySelector('[data-pne-boutique-focus-price]');
    const variantCatalogElement = root.querySelector('[data-pne-boutique-variant-catalog]');
    let variantCatalog = [];
    try { variantCatalog = JSON.parse(variantCatalogElement?.textContent || '[]'); } catch (error) { console.warn('PNE boutique variants unavailable:', error); }
    const variantWrap = root.querySelector('[data-pne-boutique-variant-wrap]');
    const variantSelect = root.querySelector('[data-pne-boutique-variant]');
    const regionSelect = root.querySelector('[data-pne-boutique-region]');
    const quantityInput = root.querySelector('[data-pne-boutique-qty]');
    const checkoutButton = root.querySelector('[data-pne-boutique-checkout]');
    const checkoutLabel = root.querySelector('[data-pne-boutique-checkout-label]');
    const checkoutNote = root.querySelector('[data-pne-boutique-checkout-note]');
    let selectedVariant = null;
    let currentTile = null;
    const modalIndex = root.querySelector('[data-pne-boutique-focus-index]');
    const modalCounter = root.querySelector('[data-pne-boutique-focus-counter]');
    const focusShell = root.querySelector('.pne-boutique__focus-shell');
    const focusCopy = root.querySelector('.pne-boutique__focus-copy');
    const distinctCount = Math.max(1, Number(root.dataset.distinct || 7));
    const minMotion = () => reduced() || isShopifyEditor();
    let motionClone = null;
    let imageSwapToken = 0;
    const modalLink = root.querySelector('[data-pne-boutique-focus-link]');
    const expand = root.querySelector('[data-pne-boutique-expand]');
    const collapse = root.querySelector('[data-pne-boutique-collapse]');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const io = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
      state.visible = entries[0]?.isIntersecting ?? true;
      if (state.visible) schedule();
      else cancel();
    }, {threshold: 0.03}) : null;
    const rows = 4, cols = 14, spread = TAU / cols;
    const state = {
      yaw: 0, pitch: -.015, zoom: 1, speed: 0, inertiaX: 0, inertiaY: 0,
      visible: true, dragging: false, justDragged: false, pointerId: null,
      lastX: 0, lastY: 0, lastMove: 0, moved: 0, raf: 0, previous: 0,
      focusIndex: -1, inputUntil: 0, fullscreen: false,
      width: 1000, height: 520, radius: 550, focal: 1200, tileW: 180, tileH: 240,
      pointerX: 0, pointerY: 0, pointerTargetX: 0, pointerTargetY: 0,
      pointerAmount: 0, pointerTargetAmount: 0
    };
    const auto = root.dataset.autoplay !== 'false' && !reduced() && !isShopifyEditor();
    const speed = clamp(Number(root.dataset.motionSpeed || 25), 10, 60);
    // Roughly one tile of drift per 12-25 seconds, enough to visibly move without distraction.
    const autoRadiansPerMs = .000014 + speed * .00000055;

    function measure() {
      state.width = Math.max(250, frame.clientWidth);
      state.height = Math.max(280, frame.clientHeight);
      const mobile = state.width < 650;
      state.tileW = clamp(mobile ? state.width * .31 : state.width * .125, mobile ? 112 : 145, mobile ? 164 : 215);
      state.tileH = state.tileW * 4 / 3;
      state.radius = state.width * (mobile ? .72 : .53);
      state.focal = state.width * (mobile ? 1.12 : 1.22);
      field.style.setProperty('--boutique-tile-width', `${state.tileW.toFixed(1)}px`);
      draw();
    }
    const wrap = a => ((a + Math.PI) % TAU + TAU) % TAU - Math.PI;

    function draw() {
      const {width, height, radius, focal, tileW, zoom, yaw, pitch} = state;
      const compact = width < 650;
      // Each tile occupies a true point on the inside of a spherical image wall.
      // Projection is performed using the camera-space distance from the viewer.
      // The wall is seamless horizontally because the azimuth is periodic.
      for (let index = 0; index < tiles.length; index++) {
        const tile = tiles[index];
        const col = index % cols;
        const row = Math.floor(index / cols) % rows;
        const a = wrap((col - (cols - 1) / 2) * spread - yaw);
        const e = (row - (rows - 1) / 2) * (compact ? .39 : .345) - pitch;
        const horizontal = Math.abs(a);
        if (horizontal > (compact ? 1.51 : 1.58) || Math.abs(e) > 1.04) {
          tile.style.visibility = 'hidden';
          tile.style.pointerEvents = 'none';
          tile.tabIndex = -1;
          continue;
        }
        tile.style.visibility = 'visible';
        const cosE = Math.cos(e), cosA = Math.cos(a);
        const depth = radius * (1 - cosA * cosE);
        const perspective = focal / (focal + depth);
        let x = Math.sin(a) * cosE * radius * perspective * zoom;
        let y = Math.sin(e) * radius * .87 * perspective * zoom;
        // A cursor pressure field makes nearby pictures part around the viewer's hand.
        // Falloff is smooth and scales with the stage; it never changes the layout flow.
        if (state.pointerAmount > .002 && state.focusIndex < 0) {
          const dx = x - state.pointerX, dy = y - state.pointerY;
          const distance = Math.hypot(dx,dy);
          const reach = compact ? 120 : 220;
          const pressure = Math.exp(-(distance * distance) / (2 * reach * reach)) * state.pointerAmount;
          const norm = Math.max(32,distance);
          x += dx / norm * pressure * (compact ? 26 : 55);
          y += dy / norm * pressure * (compact ? 17 : 35);
        }
        if (state.focusIndex >= 0) {
          // Let the room recede to the edges while the selected release is presented.
          x *= 1.19;
          y *= 1.13;
        }
        const scale = clamp(perspective * zoom * (compact ? .93 : 1.05), .22, 1.65);
        const turn = -a * 32 * (180 / Math.PI) / 90;
        const tiltX = e * 13;
        const fade = clamp((1.58 - horizontal) * 1.3, .02, 1);
        // Only affine transforms/opacity are mutated; no forced layout during each frame.
        tile.style.transform = `translate(-50%,-50%) translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0) rotateY(${turn.toFixed(2)}deg) rotateX(${tiltX.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
        tile.style.opacity = String(fade);
        tile.style.zIndex = String(Math.round(10000 - depth));
        tile.style.pointerEvents = fade > .2 && state.focusIndex < 0 ? 'auto' : 'none';
        tile.tabIndex = -1;
      }
    }
    function cancel() { if (state.raf) cancelAnimationFrame(state.raf); state.raf = 0; state.previous = 0; }
    function schedule() { if (!state.raf && state.visible && !document.hidden) state.raf = requestAnimationFrame(tick); }
    function tick(time) {
      state.raf = 0;
      const dt = state.previous ? clamp(time - state.previous, 0, 42) : 16;
      state.previous = time;
      if (!state.visible || document.hidden) return;
      state.pointerX += (state.pointerTargetX - state.pointerX) * .14;
      state.pointerY += (state.pointerTargetY - state.pointerY) * .14;
      state.pointerAmount += (state.pointerTargetAmount - state.pointerAmount) * .13;
      if (!state.dragging && state.focusIndex < 0 && !reduced()) {
        if (Math.abs(state.inertiaX) > .0000006 || Math.abs(state.inertiaY) > .0000006) {
          state.yaw += state.inertiaX * dt;
          state.pitch = clamp(state.pitch + state.inertiaY * dt, -.48, .48);
          state.inertiaX *= Math.pow(.91, dt / 16);
          state.inertiaY *= Math.pow(.88, dt / 16);
        } else if (auto && time >= state.inputUntil) {
          state.yaw += autoRadiansPerMs * dt;
        }
      }
      if (Math.abs(state.yaw) > TAU * 100) state.yaw %= TAU;
      draw();
      if (!reduced()) schedule();
    }
    function nudge(dir) {
      state.inertiaX = 0; state.inertiaY = 0;
      state.yaw += dir * spread * .9;
      state.inputUntil = now() + 950;
      draw(); schedule();
    }
    function zoom(dir) {
      state.zoom = clamp(state.zoom + dir * .13, .72, 1.55);
      state.inputUntil = now() + 1000;
      draw(); schedule();
    }
    root.querySelector('[data-pne-boutique-prev]')?.addEventListener('click', () => nudge(-1));
    root.querySelector('[data-pne-boutique-next]')?.addEventListener('click', () => nudge(1));
    root.querySelector('[data-pne-boutique-minus]')?.addEventListener('click', () => zoom(-1));
    root.querySelector('[data-pne-boutique-plus]')?.addEventListener('click', () => zoom(1));
    frame.addEventListener('pointerenter', event => {
      if (event.pointerType === 'touch' || reduced()) return;
      state.pointerTargetAmount = 1;
    });
    frame.addEventListener('pointerleave', () => {
      state.pointerTargetAmount = 0;
      state.pointerTargetX = 0;
      state.pointerTargetY = 0;
    });
    frame.addEventListener('pointerdown', event => {
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest('.pne-boutique__navigation,.pne-boutique__focus')) return;
      state.dragging = true; state.moved = 0; state.justDragged = false;
      state.pointerId = event.pointerId;
      state.lastX = event.clientX; state.lastY = event.clientY;
      state.lastMove = now(); state.inertiaX = state.inertiaY = 0;
      state.inputUntil = now() + 600;
    });
    frame.addEventListener('pointermove', event => {
      if (!state.dragging && event.pointerType !== 'touch') {
        const rect = frame.getBoundingClientRect();
        state.pointerTargetX = event.clientX - rect.left - rect.width / 2;
        state.pointerTargetY = event.clientY - rect.top - rect.height / 2;
        if (state.focusIndex < 0) state.pointerTargetAmount = 1;
        frame.style.setProperty('--cursor-x', `${((event.clientX - rect.left) / Math.max(1,rect.width) * 100).toFixed(2)}%`);
        frame.style.setProperty('--cursor-y', `${((event.clientY - rect.top) / Math.max(1,rect.height) * 100).toFixed(2)}%`);
      }
      if (!state.dragging || state.pointerId !== event.pointerId) return;
      const dx = event.clientX - state.lastX, dy = event.clientY - state.lastY;
      state.moved += Math.abs(dx) + Math.abs(dy);
      if (state.moved > 7 && !state.justDragged) {
        state.justDragged = true;
        frame.classList.add('is-dragging');
        try {frame.setPointerCapture(event.pointerId);} catch (_) {}
      }
      const dt = Math.max(12, now() - state.lastMove);
      state.lastMove = now(); state.lastX = event.clientX; state.lastY = event.clientY;
      if (!state.justDragged) return;
      const deltaX = dx / Math.max(180, state.width * .34);
      const deltaY = dy / Math.max(180, state.height * .62);
      state.yaw -= deltaX;
      state.pitch = clamp(state.pitch + deltaY * .6, -.48, .48);
      state.inertiaX = clamp(-deltaX / dt, -.008, .008);
      state.inertiaY = clamp(deltaY * .6 / dt, -.004, .004);
      draw();
    });
    function release() {
      if (!state.dragging) return;
      state.dragging = false; state.pointerId = null;
      frame.classList.remove('is-dragging');
      if (state.justDragged) setTimeout(() => {state.justDragged = false;}, 200);
      schedule();
    }
    frame.addEventListener('pointerup', release);
    frame.addEventListener('pointercancel', release);
    frame.addEventListener('lostpointercapture', release);
    frame.addEventListener('dragstart', e => e.preventDefault());
    frame.addEventListener('click', event => {
      if (state.justDragged) {event.preventDefault();event.stopPropagation();}
    }, true);
    // Normal page scrolling is never stolen. Shift+wheel zooms the gallery;
    // in explicit fullscreen mode the wheel acts as its camera zoom control.
    frame.addEventListener('wheel', event => {
      if (!event.shiftKey && !state.fullscreen) return;
      event.preventDefault();
      zoom(event.deltaY < 0 ? 1 : -1);
    }, {passive: false});
    function setFullscreen(value) {
      state.fullscreen = !!value;
      frame.classList.toggle('is-fullscreen', state.fullscreen);
      if (state.fullscreen) {
        state.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        frame.focus({preventScroll:true});
      } else {
        document.body.style.overflow = state.previousBodyOverflow ?? '';
        expand?.focus({preventScroll:true});
      }
      measure(); schedule();
    }
    expand?.addEventListener('click', () => setFullscreen(true));
    collapse?.addEventListener('click', () => setFullscreen(false));

    let previousActive = null;
    function ensureFocusHost() {
      // On phones, present the collector view in a viewport-level overlay.
      // Moving it outside the clipped gallery frame prevents the CTA being cut off.
      if (window.innerWidth < 990 && focus.parentNode !== document.body) document.body.append(focus);
      else if (window.innerWidth >= 990 && focus.parentNode !== frame) frame.append(focus);
    }
    // Shopify renders prices with .money markup so BUCKS can convert them using
    // the existing site currency selector; checkout always remains in MYR.
    function showVariantPrice(variant, tile) {
      if (!modalPrice) return;
      const html = variant?.priceHtml || tile?.dataset.productPrice || '';
      // priceHtml is generated by Shopify's money_with_currency filter, not input from a user.
      const temp = document.createElement('template');
      temp.innerHTML = html;
      const money = temp.content.querySelector('.money');
      modalPrice.replaceChildren();
      if (money) modalPrice.appendChild(money.cloneNode(true));
      else {
        const el = document.createElement('span');
        el.className = 'money';
        el.textContent = temp.content.textContent?.trim() || '—';
        modalPrice.appendChild(el);
      }
      try { window.bucksCC?.reConvert?.(); } catch(error) { console.warn('BUCKS conversion skipped',error); }
    }
    function refreshCheckoutState() {
      const quote = regionSelect?.value === 'quote';
      const eligible = Boolean(selectedVariant?.available && selectedVariant?.id);
      if (checkoutButton) checkoutButton.disabled = !quote && !eligible;
      if (checkoutLabel) checkoutLabel.textContent = quote
        ? (root.dataset.shopLocale === 'ja' ? '送料のお見積もりを依頼' : 'REQUEST SHIPPING QUOTE')
        : !eligible ? (root.dataset.shopLocale === 'ja' ? '在庫なし' : 'SOLD OUT')
        : (currentTile?.dataset.productStatus || '').toUpperCase().includes('PRE-ORDER') || (currentTile?.dataset.productStatus || '') === '予約受付中'
          ? (root.dataset.shopLocale === 'ja' ? '予約して購入手続きへ' : 'RESERVE NOW — CHECKOUT')
          : (root.dataset.shopLocale === 'ja' ? '今すぐ購入・決済へ' : 'BUY NOW — CHECKOUT');
      if (checkoutNote) checkoutNote.textContent = quote
        ? (root.dataset.shopLocale === 'ja' ? '欧州・英国などの地域は、送料確認後に請求書をご案内します。' : 'For EU, UK and unlisted regions, contact us for a shipping quote and invoice.')
        : (root.dataset.shopLocale === 'ja' ? '選択した商品をShopifyの決済画面に進めます。最終決済通貨はMYRです。' : 'Continue directly to Shopify Checkout. Final payment is in MYR.');
    }
    function selectVariant(variant) {
      selectedVariant = variant || null;
      showVariantPrice(selectedVariant, currentTile);
      refreshCheckoutState();
    }
    function setupProductOptions(tile) {
      currentTile = tile;
      const productIndex = Number(tile.dataset.productIndex);
      const productData = Number.isInteger(productIndex) ? variantCatalog[productIndex] : null;
      const variants = productData?.variants || [];
      const initial = variants.find(v => v.available) || variants[0] || null;
      if (variantSelect && variantWrap) {
        variantSelect.replaceChildren();
        variantWrap.hidden = variants.length <= 1;
        variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = variant.title === 'Default Title'
            ? (root.dataset.shopLocale === 'ja' ? '通常版' : 'Standard') : variant.title;
          option.disabled = !variant.available;
          variantSelect.appendChild(option);
        });
        if (initial) variantSelect.value = String(initial.id);
      }
      if (regionSelect) regionSelect.value = 'asia';
      if (quantityInput) quantityInput.value = '1';
      selectVariant(initial);
    }
    variantSelect?.addEventListener('change', () => {
      const data = variantCatalog[Number(currentTile?.dataset.productIndex)];
      selectVariant(data?.variants?.find(v => String(v.id) === variantSelect.value) || null);
    });
    regionSelect?.addEventListener('change', refreshCheckoutState);
    checkoutButton?.addEventListener('click', () => {
      if (!currentTile) return;
      if (regionSelect?.value === 'quote') {
        const message = `Hello, I would like to request an international shipping quote for: ${currentTile.dataset.productTitle || ''}\n${new URL(currentTile.dataset.productUrl || '/',location.origin).href}`;
        window.open(`https://wa.me/60125775902?text=${encodeURIComponent(message)}`, '_blank','noopener,noreferrer');
        return;
      }
      if (!selectedVariant?.available || !/^\d+$/.test(String(selectedVariant.id))) return;
      const qty = Math.min(99,Math.max(1,parseInt(quantityInput?.value || '1',10) || 1));
      const base = window.Shopify?.routes?.root || '/';
      const rootPath = base.endsWith('/') ? base : base + '/';
      const permalink = `${rootPath}cart/${selectedVariant.id}:${qty}`;
      // Shopify's cart permalink opens a fresh checkout with this exact variant.
      checkoutButton.disabled = true;
      if (checkoutLabel) checkoutLabel.textContent = root.dataset.shopLocale === 'ja' ? '決済画面を開いています…' : 'OPENING SECURE CHECKOUT…';
      window.location.assign(permalink);
    });
    function animateSourceToStage(tile) {
      if (minMotion() || !tile || !modalImage || !window.Element?.prototype?.animate) return;
      const source = tile.getBoundingClientRect();
      const target = modalImage.getBoundingClientRect();
      if (!source.width || !target.width) return;
      motionClone?.remove();
      const clone = document.createElement('div');
      clone.className = 'pne-boutique__morph-clone';
      clone.style.cssText = `position:fixed;left:${source.left}px;top:${source.top}px;width:${source.width}px;height:${source.height}px;z-index:2147483600;pointer-events:none;overflow:hidden;transform-origin:top left;background:#0a0b0d;box-shadow:0 20px 55px #0009;`;
      const img = tile.querySelector('img')?.cloneNode(true);
      if (img) {
        img.removeAttribute('srcset');
        img.src = tile.dataset.productImage || img.src;
        img.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block';
        clone.append(img);
      }
      document.body.append(clone);
      motionClone = clone;
      const dx = target.left - source.left, dy = target.top - source.top;
      const sx = target.width / source.width, sy = target.height / source.height;
      const animation = clone.animate([
        {transform:'translate3d(0,0,0) scale(1,1)', opacity:1,filter:'brightness(1)'},
        {transform:`translate3d(${dx}px,${dy}px,0) scale(${sx},${sy})`,opacity:0,filter:'brightness(1.14)'}
      ], {duration:560,easing:'cubic-bezier(.18,.72,.16,1)',fill:'forwards'});
      animation.finished.catch(()=>{}).then(()=>{clone.remove();if(motionClone===clone)motionClone=null;});
    }
    function applyProduct(tile, switching = false) {
      const rawIndex = Number(tile.dataset.tileIndex);
      state.focusIndex = Number.isFinite(rawIndex) ? rawIndex : 0;
      const src = tile.dataset.productImage || tile.querySelector('img')?.src || '';
      const title = tile.dataset.productTitle || '';
      const token = ++imageSwapToken;
      const update = () => {
        modalTitle.textContent = title;
        modalTitle.classList.toggle('is-long', title.length > 52);
        modalStatus.textContent = tile.dataset.productStatus || '';
        setupProductOptions(tile);
        modalImage.src = src;
        modalImage.alt = title;
        modalLink.href = tile.dataset.productUrl || '/collections/all';
        const count = `${String((state.focusIndex % distinctCount) + 1).padStart(2,'0')} / ${String(distinctCount).padStart(2,'0')}`;
        if (modalIndex) modalIndex.textContent = count;
        if (modalCounter) modalCounter.textContent = count;
      };
      if (!switching || minMotion()) {update();return;}
      // Cross-dissolve the new art and copy; never snap the image source mid-frame.
      const imageAnim = modalImage.animate([
        {opacity:1,transform:'translateX(0) scale(1)'},
        {opacity:0,transform:'translateX(-18px) scale(.975)'}
      ], {duration:155,easing:'ease-in',fill:'forwards'});
      imageAnim.finished.catch(()=>{}).then(()=>{
        if (token !== imageSwapToken) return;
        update();
        imageAnim.cancel();
        modalImage.animate([
          {opacity:0,transform:'translateX(26px) scale(1.035)'},
          {opacity:1,transform:'translateX(0) scale(1)'}
        ], {duration:470,easing:'cubic-bezier(.17,.73,.13,1)'});
        focusCopy?.animate([
          {opacity:.66,transform:'translateY(8px)'},
          {opacity:1,transform:'translateY(0)'}
        ],{duration:420,easing:'ease-out'});
      });
    }
    function showProduct(tile) {
      if (!tile || !focus) return;
      const switching = state.focusIndex >= 0;
      if (!switching) previousActive = document.activeElement;
      ensureFocusHost();
      applyProduct(tile, switching);
      if (switching) return;
      if (window.innerWidth < 990) {
        state.previousFocusOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
      state.pointerTargetAmount = 0;
      focus.classList.add('is-open');
      field.classList.add('is-focused');
      if (!minMotion()) field.classList.add('is-settling');
      setTimeout(() => field.classList.remove('is-settling'), 650);
      focus.setAttribute('aria-hidden', 'false');
      draw();
      cancel();
      if (!minMotion()) requestAnimationFrame(() => animateSourceToStage(tile));
      focus.querySelector('.pne-boutique__focus-close')?.focus({preventScroll:true});
    }
    function hideProduct() {
      if (state.focusIndex < 0) return;
      imageSwapToken++;
      motionClone?.remove();motionClone=null;
      state.focusIndex = -1;
      focus.classList.remove('is-open');
      if (!minMotion()) field.classList.add('is-settling');
      field.classList.remove('is-focused');
      setTimeout(() => field.classList.remove('is-settling'), 650);
      focus.setAttribute('aria-hidden', 'true');
      if (state.previousFocusOverflow !== undefined) {document.body.style.overflow = state.previousFocusOverflow;state.previousFocusOverflow=undefined;}
      if (previousActive && previousActive.isConnected) previousActive.focus({preventScroll:true});
      previousActive = null;
      state.inputUntil = now() + 500;
      draw(); schedule();
    }
    tiles.forEach(tile => tile.addEventListener('click', () => showProduct(tile)));
    focus.querySelectorAll('[data-pne-boutique-dismiss]').forEach(button => button.addEventListener('click', hideProduct));
    function focusStep(step) {
      if (state.focusIndex < 0) return;
      // Skip repeated tiles representing the same product where possible.
      const distinct = clamp(Number(root.dataset.distinct || 10), 1, tiles.length);
      const nextIndex = ((state.focusIndex + step) % distinct + distinct) % distinct;
      showProduct(tiles[nextIndex]);
    }
    focus.querySelector('[data-pne-boutique-focus-prev]')?.addEventListener('click', () => focusStep(-1));
    focus.querySelector('[data-pne-boutique-focus-next]')?.addEventListener('click', () => focusStep(1));
    function onKeyboard(event) {
      if (event.key === 'Escape') {
        if (state.focusIndex >= 0) hideProduct();
        else if (state.fullscreen) setFullscreen(false);
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        if (state.focusIndex >= 0) focusStep(event.key === 'ArrowLeft' ? -1 : 1);
        else nudge(event.key === 'ArrowLeft' ? -1 : 1);
      }
      if (event.key === 'Enter' && state.focusIndex < 0 && event.target === frame) {
        event.preventDefault();
        const centre = [...tiles].filter(t => t.style.visibility !== 'hidden').sort((a,b) => Math.abs(Number(b.style.zIndex)) - Math.abs(Number(a.style.zIndex)));
        showProduct(centre[0] || tiles[0]);
      }
      if (event.key === '+' || event.key === '=') {event.preventDefault();zoom(1);}
      if (event.key === '-') {event.preventDefault();zoom(-1);}
    }
    frame.addEventListener('keydown', onKeyboard);
    focus.addEventListener('keydown', onKeyboard);
    const observer = new MutationObserver(() => {if (!document.contains(root)) cleanup();});
    function cleanup() {
      cancel(); motionClone?.remove(); io?.disconnect(); ro?.disconnect(); observer.disconnect();
      if (focus.parentNode === document.body) focus.remove();
      if (state.previousFocusOverflow !== undefined) document.body.style.overflow = state.previousFocusOverflow;
      if (state.fullscreen) document.body.style.overflow = state.previousBodyOverflow ?? '';
    }
    ro?.observe(frame);
    io?.observe(root);
    measure(); schedule();
    root.addEventListener('shopify:section:unload', cleanup, {once:true});
    // Shopify theme editor may remove/reinsert sections; the editor event cleans up.
    if (isShopifyEditor()) observer.observe(root.parentElement || document.body, {childList:true});
    document.addEventListener('visibilitychange', () => {if (document.hidden) cancel();else schedule();});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), {once:true});
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
